import './App.css';
import './i18n'; // Initialize i18next
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import VisitorTracker from './components/VisitorTracker';
import InactivityLogout from './components/InactivityLogout';
import TabTimeTracker from './components/TabTimeTracker';
import Home from './pages/Home';
import Schemes from './pages/Schemes';
import SchemeDetails from './pages/SchemeDetails';
import SavedSchemes from './pages/SavedSchemes';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminDashboard from './pages/AdminDashboard';
import HelpMeApply from './pages/HelpMeApply';
import NewsPage from './pages/NewsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import FuturePage from './pages/FuturePage';
import AdminLogin from './pages/AdminLogin';
import AdminLoginSimple from './pages/AdminLoginSimple';
import AdminToken from './pages/AdminToken';
import ImpactAnalyser from './pages/ImpactAnalyser';
import ImpactResults from './pages/ImpactResults';
import IncomeIntelligence from './pages/IncomeIntelligence';
import MoneyHub from './pages/MoneyHub';
import KmsServices from './pages/KmsServices';
import ServiceDetail from './pages/ServiceDetail';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * Strip trailing slashes (except root) so /schemes/ and /schemes resolve
 * to the same canonical URL — matches the canonical we declare in <head>.
 * Done synchronously *before* BrowserRouter initialises so child components
 * never see the trailing-slash variant.
 */
(function normaliseTrailingSlashOnLoad() {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;
  if (pathname.length > 1 && pathname.endsWith('/')) {
    const cleaned = pathname.replace(/\/+$/, '') + search + hash;
    window.history.replaceState(null, '', cleaned);
  }
})();

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <LanguageProvider>
          <AuthProvider>
            <VisitorTracker />
            <InactivityLogout />
            <TabTimeTracker />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/admin-access" element={<AdminLoginSimple />} />
              <Route path="/admin-token" element={<AdminToken />} />
              
              {/* Home page - Public (accessible without login) */}
              <Route path="/" element={<Home />} />
              
              {/* Public info pages */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/future" element={<FuturePage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* Public browsing — schemes/jobs (with auth popup gating) and news */}
              <Route path="/schemes" element={<Schemes />} />
              <Route path="/schemes/jobs/:jobSlug" element={<Schemes />} />
              <Route path="/schemes/:slug" element={<SchemeDetails />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/impact-analyser" element={<ImpactAnalyser />} />
              <Route path="/money-hub" element={<KmsServices />} />
              <Route path="/money-hub/savings" element={<MoneyHub />} />
              <Route path="/money-hub/:slug" element={<ServiceDetail />} />

              {/* Protected routes - Require authentication */}
              <Route path="/saved-schemes" element={
                <ProtectedRoute>
                  <SavedSchemes />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="/apply-help" element={
                <ProtectedRoute>
                  <HelpMeApply />
                </ProtectedRoute>
              } />

              {/* Impact Analyser results — require auth */}
              <Route path="/impact-analyser/results" element={
                <ProtectedRoute>
                  <ImpactResults />
                </ProtectedRoute>
              } />
              <Route path="/income-intelligence" element={
                <ProtectedRoute>
                  <IncomeIntelligence />
                </ProtectedRoute>
              } />
              
              {/* Subscription page */}
              <Route path="/subscription" element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              } />
              
              {/* Admin only route */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />
            </Routes>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
